import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Smartphone, CheckCircle, XCircle, Leaf, Home, Compass, Gift, ShieldCheck, Star, Sparkles, Heart, Clock, BookOpen, MessageCircle, AlertTriangle, Users, Calendar, ChevronDown, ChevronUp, ArrowRight, Zap, Lock, Download, ShoppingCart } from 'lucide-react';
// ─── Scroll to pricing helper ─────────────────────────────────────────────────
function scrollToPricing() {
  const el = document.getElementById('pricing-section');
  if (el) el.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
// ─── Animation wrapper ────────────────────────────────────────────────────────
function FadeUp({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 28
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.65,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} className={className} data-id="element-2">
      {children}
    </motion.div>;
}
// ─── Progress Bar ─────────────────────────────────────────────────────────────
function ProgressBar() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const update = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? scrollTop / docHeight * 100 : 0);
    };
    window.addEventListener('scroll', update, {
      passive: true
    });
    return () => window.removeEventListener('scroll', update);
  }, []);
  return <div className="progress-bar" style={{
    width: `${progress}%`
  }} data-id="element-3" />;
}
// ─── Star Rating ──────────────────────────────────────────────────────────────
function StarRating({
  count = 5
}: {
  count?: number;
}) {
  return <div className="flex gap-0.5" data-id="element-4">
      {Array.from({
      length: count
    }).map((_, i) => <Star key={i} size={16} fill="#D4956A" stroke="#D4956A" data-id="element-5" />)}
    </div>;
}
// ─── CTA Button ───────────────────────────────────────────────────────────────
function CTAButton({
  label = 'Quiero Recuperar la Paz en Mi Hogar',
  size = 'lg',
  showTrust = true,
  href
}: {
  label?: string;
  size?: 'lg' | 'md';
  showTrust?: boolean;
  href?: string;
}) {
  const handleClick = () => {
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      scrollToPricing();
    }
  };
  return <div className="flex flex-col items-center gap-3" data-id="element-6">
      <motion.button whileHover={{
      scale: 1.03
    }} whileTap={{
      scale: 0.97
    }} onClick={handleClick} className={`cta-pulse font-lato font-bold text-white rounded-full cursor-pointer border-0 transition-colors flex items-center gap-2
          ${size === 'lg' ? 'text-lg md:text-xl px-10 py-5' : 'text-base px-8 py-4'}`} style={{
      backgroundColor: '#8B2635'
    }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#6B1D29'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#8B2635'} data-id="element-7">
        {label}
        <ArrowRight size={20} data-id="element-8" />
      </motion.button>
      {showTrust && <div className="flex items-center gap-4 flex-wrap justify-center" data-id="element-9">
          <span className="flex items-center gap-1 font-lato text-xs" style={{
        color: '#7A6055'
      }} data-id="element-10">
            <Zap size={12} data-id="element-11" /> Acceso inmediato
          </span>
          <span className="flex items-center gap-1 font-lato text-xs" style={{
        color: '#7A6055'
      }} data-id="element-12">
            <Download size={12} data-id="element-13" /> Descarga digital
          </span>
          <span className="flex items-center gap-1 font-lato text-xs" style={{
        color: '#7A6055'
      }} data-id="element-14">
            <ShieldCheck size={12} data-id="element-15" /> Garantía 7 días
          </span>
        </div>}
    </div>;
}
// ─── Inline CTA Strip ─────────────────────────────────────────────────────────
function CTAStrip({
  label,
  bg = '#FDF8F3'
}: {
  label: string;
  bg?: string;
}) {
  return <section className="py-10 px-4" style={{
    backgroundColor: bg
  }} data-id="element-16">
      <FadeUp data-id="element-17">
        <div className="max-w-2xl mx-auto flex justify-center" data-id="element-18">
          <CTAButton label={label} size="md" showTrust={false} data-id="element-19" />
        </div>
      </FadeUp>
    </section>;
}
// ─── Divider ──────────────────────────────────────────────────────────────────
function Divider() {
  return <div className="flex items-center justify-center gap-3 py-6 px-4" style={{
    color: '#D4956A',
    opacity: 0.7
  }} data-id="element-20">
      <Sparkles size={16} data-id="element-21" />
      <Sparkles size={16} data-id="element-22" />
      <Sparkles size={16} data-id="element-23" />
    </div>;
}
// ─── Social Proof Pop Notification ────────────────────────────────────────────
const POP_NAMES = ['Jennifer M.', 'Sandra K.', 'Maria L.', 'Patricia N.', 'Ana G.', 'Laura R.', 'Carmen S.', 'Rosa D.', 'Isabella V.', 'Gabriela T.', 'Sofia P.', 'Valentina H.', 'Daniela F.', 'Alejandra C.', 'Natalia B.', 'Andrea W.', 'Camila J.', 'Lucia E.', 'Elena M.', 'Diana R.', 'Monica A.', 'Teresa L.', 'Claudia V.', 'Adriana S.'];
const POP_CITIES = ['Miami, FL', 'Houston, TX', 'Los Angeles, CA', 'Chicago, IL', 'New York, NY', 'Phoenix, AZ', 'San Antonio, TX', 'Dallas, TX', 'San Diego, CA', 'Orlando, FL', 'Denver, CO', 'Las Vegas, NV', 'Atlanta, GA', 'Tampa, FL', 'Austin, TX', 'Charlotte, NC', 'San Jose, CA', 'Jacksonville, FL', 'Fort Worth, TX', 'El Paso, TX'];
function SocialProofPop() {
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState({
    name: '',
    city: ''
  });
  useEffect(() => {
    const pickRandom = () => {
      setData({
        name: POP_NAMES[Math.floor(Math.random() * POP_NAMES.length)],
        city: POP_CITIES[Math.floor(Math.random() * POP_CITIES.length)]
      });
      setVisible(true);
      setTimeout(() => setVisible(false), 4500);
    };
    const initialTimer = setTimeout(pickRandom, 15000);
    const interval = setInterval(pickRandom, 15000);
    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, []);
  return <AnimatePresence data-id="element-24">
      {visible && <motion.div initial={{
      opacity: 0,
      y: 80,
      x: 0
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 80
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className="fixed bottom-4 left-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl max-w-sm" style={{
      backgroundColor: 'white',
      border: '2px solid #D4956A',
      boxShadow: '0 8px 32px rgba(44,24,16,0.18)'
    }} data-id="element-25">
          <img src="https://cdn.magicpatterns.com/uploads/uFzzJa9ATsUPFd7QYApHaH/portada.png" alt="Celular Bajo Control" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" style={{
        border: '1px solid #F5E6D3'
      }} data-id="element-26" />
          <div className="flex-1 min-w-0" data-id="element-27">
            <p className="font-lato font-bold text-sm truncate" style={{
          color: '#2C1810'
        }} data-id="element-28">
              {data.name} <span className="ml-1" data-id="element-29">🇺🇸</span>
            </p>
            <p className="font-lato text-xs" style={{
          color: '#7A6055'
        }} data-id="element-30">
              {data.city}
            </p>
            <p className="font-lato text-xs mt-0.5" style={{
          color: '#4A7C59'
        }} data-id="element-31">
              <ShoppingCart size={11} className="inline mr-1" style={{
            verticalAlign: '-1px'
          }} data-id="element-32" />
              Compró por{' '}
              <strong style={{
            color: '#8B2635'
          }} data-id="element-33">
                $3.99
              </strong>
            </p>
          </div>
          <button onClick={() => setVisible(false)} className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold" style={{
        backgroundColor: '#F5E6D3',
        color: '#7A6055',
        border: '1px solid #D4956A'
      }} data-id="element-34">
            ×
          </button>
        </motion.div>}
    </AnimatePresence>;
}
// ─── Main Sales Page ──────────────────────────────────────────────────────────
export function SalesPage() {
  return <div className="w-full min-h-screen" style={{
    backgroundColor: '#FDF8F3',
    color: '#2C1810'
  }} data-id="element-35">
      <ProgressBar data-id="element-36" />
      <SocialProofPop data-id="element-37" />

      {/* ── ANNOUNCEMENT BAR ── */}
      <div className="announcement-bar flex items-center justify-center gap-2" data-id="element-38">
        <Smartphone size={15} data-id="element-39" />
        <span data-id="element-40">
          Para mamás con hijos adolescentes | Guía digital descargable
        </span>
      </div>

      {/* ── HERO ── */}
      <section className="section-padding" style={{
      background: 'linear-gradient(180deg, #F5E6D3 0%, #FDF8F3 100%)'
    }} data-id="element-41">
        <div className="max-w-5xl mx-auto" data-id="element-42">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16" data-id="element-43">
            <div className="flex-1 text-center md:text-left" data-id="element-44">
              <FadeUp data-id="element-45">
                <p className="font-lato font-bold uppercase tracking-widest text-sm mb-4" style={{
                color: '#D4956A'
              }} data-id="element-46">
                  Si tu hijo/a tiene entre 10 y 18 años y el celular se ha
                  convertido en una batalla diaria…
                </p>
              </FadeUp>
              <FadeUp delay={0.1} data-id="element-47">
                <h1 className="font-playfair italic font-bold leading-tight mb-6" style={{
                color: '#8B2635',
                fontSize: 'clamp(1.9rem, 4.5vw, 3rem)'
              }} data-id="element-48">
                  Cómo Recuperar la Paz en Tu Hogar y la Conexión con Tu
                  Adolescente en 30 Días…
                  <span className="block mt-2 not-italic font-normal" style={{
                  fontSize: '0.68em',
                  color: '#2C1810'
                }} data-id="element-49">
                    Sin Gritos, Sin Guerras por el Celular, y Sin Sentirte la
                    Villana de la Historia
                  </span>
                </h1>
              </FadeUp>
              <FadeUp delay={0.2} data-id="element-50">
                <p className="font-lato text-lg mb-8" style={{
                color: '#5A3E35'
              }} data-id="element-51">
                  El plan paso a paso que miles de mamás están usando para poner
                  límites reales con el celular — y volver a disfrutar de su
                  hijo/a
                </p>
              </FadeUp>
              <FadeUp delay={0.3} data-id="element-52">
                <CTAButton label="Quiero Recuperar la Paz en Mi Hogar" data-id="element-53" />
              </FadeUp>
            </div>
            <FadeUp delay={0.2} className="flex-shrink-0 flex justify-center" data-id="element-54">
              <div className="relative" data-id="element-55">
                <div className="absolute inset-0 rounded-2xl blur-2xl opacity-30" style={{
                backgroundColor: '#D4956A',
                transform: 'scale(0.9) translateY(8%)'
              }} data-id="element-56" />
                <motion.img src="https://cdn.magicpatterns.com/uploads/uFzzJa9ATsUPFd7QYApHaH/portada.png" alt="Celular Bajo Control, Hogar en Paz — portada del libro" className="relative rounded-xl" style={{
                width: 'clamp(220px, 30vw, 320px)',
                boxShadow: '8px 16px 48px rgba(44,24,16,0.28), 2px 4px 12px rgba(44,24,16,0.15)',
                transform: 'rotate(-2deg)'
              }} initial={{
                opacity: 0,
                rotate: -5,
                y: 20
              }} animate={{
                opacity: 1,
                rotate: -2,
                y: 0
              }} transition={{
                duration: 0.8,
                delay: 0.4,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-57" />
                <motion.div className="absolute -top-3 -right-3 font-lato font-bold text-white text-xs px-3 py-2 rounded-full shadow-lg" style={{
                backgroundColor: '#4A7C59',
                lineHeight: 1.3
              }} initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.9,
                type: 'spring',
                stiffness: 260
              }} data-id="element-58">
                  <div className="text-center" data-id="element-59">
                    <div data-id="element-60">¡PRECIO</div>
                    <div data-id="element-61">LANZAMIENTO!</div>
                  </div>
                </motion.div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ── HOOK / LEAD ── */}
      <section className="section-padding" data-id="element-62">
        <div className="max-w-2xl mx-auto" data-id="element-63">
          <FadeUp data-id="element-64">
            <p className="font-playfair italic text-2xl mb-8" style={{
            color: '#8B2635'
          }} data-id="element-65">
              Querida mamá,
            </p>
          </FadeUp>
          <FadeUp delay={0.1} data-id="element-66">
            <p className="mb-5" data-id="element-67">
              ¿Recuerdas la última vez que tuviste una conversación tranquila
              con tu hijo/a… sin que el celular estuviera de por medio?
            </p>
            <p className="mb-5" data-id="element-68">
              ¿Cuándo fue la última vez que cenaron juntos sin que él o ella
              estuviera con los ojos pegados a la pantalla?
            </p>
            <p className="mb-5 font-bold" style={{
            color: '#8B2635'
          }} data-id="element-69">
              Si tienes que pensar mucho para recordarlo… esto es para ti.
            </p>
            <p className="mb-5" data-id="element-70">
              Porque lo que estás viviendo no es un problema de disciplina. No
              es que seas mala mamá. Y definitivamente no es que tu hijo/a sea
              un caso perdido.
            </p>
            <p className="mb-5" data-id="element-71">
              Lo que está pasando en tu hogar es una{' '}
              <strong data-id="element-72">crisis silenciosa</strong> que afecta a millones de
              familias — y la mayoría de los consejos que existen simplemente no
              funcionan para la realidad de hoy.
            </p>
          </FadeUp>
          <FadeUp delay={0.15} data-id="element-73">
            <div className="pull-quote" data-id="element-74">
              "Nadie te preparó para criar a un adolescente en la era de los
              smartphones. Y eso no es tu culpa."
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── PAIN AMPLIFICATION ── */}
      <section className="section-padding" style={{
      backgroundColor: '#F5E6D3'
    }} data-id="element-75">
        <div className="max-w-2xl mx-auto" data-id="element-76">
          <FadeUp data-id="element-77">
            <p className="font-lato font-bold uppercase tracking-widest text-sm mb-6 text-center" style={{
            color: '#D4956A'
          }} data-id="element-78">
              ¿Te suena familiar alguna de estas situaciones?
            </p>
          </FadeUp>
          <div className="space-y-5" data-id="element-79">
            {['Le dices que apague el celular y empieza la guerra. Gritos, portazos, lágrimas — y al final tú cedes porque no aguantas más el conflicto.', 'Sientes que compites con una pantalla por la atención de tu propio hijo/a. Y la pantalla siempre gana.', 'Has intentado quitarle el celular y el ambiente en casa se volvió insoportable. Días de silencio, miradas de odio, y tú sintiéndote culpable.', 'Ves cómo se aleja cada vez más. Las conversaciones son monosílabos. Ya no te cuenta nada. Y tienes miedo de perder esa conexión para siempre.', 'Te preguntas si estás haciendo algo mal. Si eres demasiado estricta o demasiado permisiva. Si deberías ceder más o exigir más.', 'Buscas consejos en internet y todos se contradicen. Unos dicen que pongas límites duros. Otros dicen que no lo presiones. Y tú sigues igual de perdida.'].map((pain, i) => <FadeUp key={i} delay={i * 0.07} data-id="element-80">
                <div className="flex gap-4 items-start" data-id="element-81">
                  <CheckCircle size={22} className="flex-shrink-0 mt-0.5" style={{
                color: '#8B2635'
              }} data-id="element-82" />
                  <p className="font-lato" style={{
                color: '#2C1810'
              }} data-id="element-83">
                    {pain}
                  </p>
                </div>
              </FadeUp>)}
          </div>
          <FadeUp delay={0.5} data-id="element-84">
            <div className="mt-10 p-6 rounded-xl" style={{
            backgroundColor: 'white',
            border: '2px solid #D4956A'
          }} data-id="element-85">
              <p className="mb-3" data-id="element-86">
                Si marcaste aunque sea 2 de estas situaciones… quiero que sepas
                algo importante:
              </p>
              <p className="font-bold text-xl" style={{
              color: '#8B2635'
            }} data-id="element-87">
                No estás sola. Y no es tu culpa.
              </p>
              <p className="mt-3" data-id="element-88">
                El problema no es tu hijo/a. El problema no eres tú. El problema
                es que <strong data-id="element-89">nadie te enseñó cómo navegar esto.</strong>
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <CTAStrip label="Sí, Quiero Este Plan Ahora" bg="#FDF8F3" data-id="element-90" />

      {/* ── AGITATION ── */}
      <section className="section-padding" data-id="element-91">
        <div className="max-w-2xl mx-auto" data-id="element-92">
          <FadeUp data-id="element-93">
            <h2 className="font-playfair italic text-3xl mb-6" style={{
            color: '#8B2635'
          }} data-id="element-94">
              Lo que nadie te dice sobre ignorar este problema…
            </h2>
          </FadeUp>
          <FadeUp delay={0.1} data-id="element-95">
            <p className="mb-5" data-id="element-96">
              Imagina que pasan otros 6 meses así. Un año. Dos años.
            </p>
            <p className="mb-5" data-id="element-97">
              Tu hijo/a sigue creciendo — pero <strong data-id="element-98">lejos de ti</strong>. Las
              conversaciones se vuelven cada vez más cortas. Las peleas por el
              celular se vuelven peleas por todo. Y un día te das cuenta de que
              ya no sabes quién es tu hijo/a realmente.
            </p>
            <p className="mb-5" data-id="element-99">
              No porque sea mala persona. Sino porque en los años más
              importantes de su vida, la conexión entre ustedes se fue
              rompiendo… poco a poco… pelea tras pelea… pantalla tras pantalla.
            </p>
          </FadeUp>
          <FadeUp delay={0.2} data-id="element-100">
            <div className="pull-quote" data-id="element-101">
              "La adolescencia dura unos pocos años. La distancia que se crea en
              esos años puede durar toda la vida."
            </div>
          </FadeUp>
          <FadeUp delay={0.25} data-id="element-102">
            <p className="mb-5" data-id="element-103">
              Y lo más doloroso no es el celular. Lo más doloroso es saber que{' '}
              <strong data-id="element-104">el tiempo se está acabando</strong> — que cada día que
              pasa es un día menos de tenerlo/a en casa, de poder influir en
              quién se está convirtiendo.
            </p>
            <p className="mb-5" data-id="element-105">
              Pero también hay otra posibilidad. Una que miles de mamás ya están
              viviendo.
            </p>
          </FadeUp>
        </div>
      </section>

      <CTAStrip label="Quiero Dejar de Pelear por el Celular" bg="#F5E6D3" data-id="element-106" />

      {/* ── DREAM OUTCOME ── */}
      <section className="section-padding" style={{
      backgroundColor: '#EAF2EC'
    }} data-id="element-107">
        <div className="max-w-2xl mx-auto" data-id="element-108">
          <FadeUp data-id="element-109">
            <p className="font-lato font-bold uppercase tracking-widest text-sm mb-4 text-center" style={{
            color: '#4A7C59'
          }} data-id="element-110">
              Imagina esto…
            </p>
            <h2 className="font-playfair italic text-3xl mb-8 text-center" style={{
            color: '#4A7C59'
          }} data-id="element-111">
              Cómo se ve tu vida cuando esto está resuelto
            </h2>
          </FadeUp>
          <div className="space-y-4" data-id="element-112">
            {['Tu hijo/a apaga el celular sin drama cuando llega la hora de cenar — porque entiende el porqué, no porque tenga miedo de ti.', 'Tienes conversaciones reales con él/ella. Te cuenta cosas. Se ríen juntos. Vuelves a sentir esa conexión que creías perdida.', 'Pones límites con el celular sin gritar, sin culpa, y sin sentirte la villana. Y funcionan.', 'El ambiente en casa es tranquilo. Ya no hay esa tensión constante que te agotaba. Puedes respirar.', 'Sabes exactamente qué decir y cómo decirlo cuando surge un conflicto por las pantallas — y lo resuelves en minutos, no en días.', 'Te sientes segura de tu rol como mamá. Confiada. En paz contigo misma.'].map((dream, i) => <FadeUp key={i} delay={i * 0.07} data-id="element-113">
                <div className="flex gap-4 items-start" data-id="element-114">
                  <Heart size={20} className="flex-shrink-0 mt-1" style={{
                color: '#4A7C59'
              }} fill="#4A7C59" data-id="element-115" />
                  <p className="font-lato" data-id="element-116">{dream}</p>
                </div>
              </FadeUp>)}
          </div>
          <FadeUp delay={0.5} data-id="element-117">
            <div className="mt-10 text-center" data-id="element-118">
              <p className="font-playfair italic text-2xl" style={{
              color: '#4A7C59'
            }} data-id="element-119">
                Esto no es un sueño. Es lo que pasa cuando tienes el plan
                correcto.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <CTAStrip label="Quiero Esta Transformación Para Mi Familia" bg="#FDF8F3" data-id="element-120" />

      {/* ── WHY OTHER SOLUTIONS FAIL ── */}
      <section className="section-padding" data-id="element-121">
        <div className="max-w-2xl mx-auto" data-id="element-122">
          <FadeUp data-id="element-123">
            <h2 className="font-playfair italic text-3xl mb-6" style={{
            color: '#8B2635'
          }} data-id="element-124">
              Por qué todo lo que has intentado hasta ahora no ha funcionado
            </h2>
          </FadeUp>
          <FadeUp delay={0.1} data-id="element-125">
            <p className="mb-6" data-id="element-126">
              Probablemente ya has intentado varias cosas. Y no te ha
              funcionado. No porque seas mala mamá — sino porque las soluciones
              que existen están diseñadas para un problema diferente al que
              realmente tienes.
            </p>
          </FadeUp>
          {[{
          icon: <XCircle size={20} style={{
            color: '#8B2635'
          }} className="flex-shrink-0 mt-0.5" data-id="element-127" />,
          title: 'Quitarle el celular como castigo',
          body: 'Funciona por 3 días. Luego el conflicto regresa — más intenso que antes. Y tú terminas cediendo para recuperar la paz. El ciclo se repite.'
        }, {
          icon: <XCircle size={20} style={{
            color: '#8B2635'
          }} className="flex-shrink-0 mt-0.5" data-id="element-128" />,
          title: 'Las apps de control parental',
          body: 'Tu hijo/a aprende a saltarlas en 20 minutos. O las cumple con resentimiento. El problema de fondo — la falta de acuerdo y conexión — sigue intacto.'
        }, {
          icon: <XCircle size={20} style={{
            color: '#8B2635'
          }} className="flex-shrink-0 mt-0.5" data-id="element-129" />,
          title: 'Los consejos de internet',
          body: 'Contradictorios, genéricos, escritos por personas que no conocen a tu hijo/a ni tu realidad. Lees 10 artículos y terminas más confundida que antes.'
        }, {
          icon: <XCircle size={20} style={{
            color: '#8B2635'
          }} className="flex-shrink-0 mt-0.5" data-id="element-130" />,
          title: 'Hablar con él/ella "en serio"',
          body: 'Sin la estructura correcta, esa conversación termina en pelea o en silencio. No es lo que dices — es cómo lo dices y cuándo lo dices.'
        }, {
          icon: <XCircle size={20} style={{
            color: '#8B2635'
          }} className="flex-shrink-0 mt-0.5" data-id="element-131" />,
          title: 'Esperar a que "se le pase"',
          body: 'No se le pasa. Los hábitos digitales se consolidan. Y la distancia entre ustedes crece. El tiempo que no recuperas hoy, no lo recuperas mañana.'
        }].map((item, i) => <FadeUp key={i} delay={i * 0.08} data-id="element-132">
              <div className="module-card mb-4" data-id="element-133">
                <div className="flex gap-3 items-start mb-1" data-id="element-134">
                  {item.icon}
                  <p className="font-bold font-lato" style={{
                color: '#8B2635'
              }} data-id="element-135">
                    {item.title}
                  </p>
                </div>
                <p className="font-lato pl-8" style={{
              color: '#5A3E35'
            }} data-id="element-136">
                  {item.body}
                </p>
              </div>
            </FadeUp>)}
          <FadeUp delay={0.5} data-id="element-137">
            <div className="pull-quote mt-8" data-id="element-138">
              "El problema no es la falta de voluntad. Es la falta de un método
              diseñado específicamente para esto."
            </div>
          </FadeUp>
        </div>
      </section>

      <CTAStrip label="Dame el Método que Sí Funciona" bg="#F5E6D3" data-id="element-139" />

      {/* ── UNIQUE MECHANISM ── */}
      <section className="section-padding" style={{
      backgroundColor: '#F5E6D3'
    }} data-id="element-140">
        <div className="max-w-2xl mx-auto" data-id="element-141">
          <FadeUp data-id="element-142">
            <p className="font-lato font-bold uppercase tracking-widest text-sm mb-4" style={{
            color: '#D4956A'
          }} data-id="element-143">
              La pieza que faltaba
            </p>
            <h2 className="font-playfair italic text-3xl mb-6" style={{
            color: '#8B2635'
          }} data-id="element-144">
              La verdadera razón por la que el celular se convirtió en una
              batalla
            </h2>
          </FadeUp>
          <FadeUp delay={0.1} data-id="element-145">
            <p className="mb-5" data-id="element-146">
              Aquí está lo que la mayoría de los expertos no te dicen:
            </p>
            <p className="mb-5" data-id="element-147">
              El problema no es el celular. El celular es solo el síntoma.
            </p>
            <p className="mb-5" data-id="element-148">
              La raíz del problema es una <strong data-id="element-149">brecha de conexión</strong> —
              un espacio que se abrió entre tú y tu hijo/a, y que el celular
              llenó. Cuando un adolescente siente que no es comprendido, que sus
              emociones no tienen espacio en casa, que las conversaciones
              siempre terminan en conflicto… busca ese espacio en otro lado. Y
              el celular siempre está disponible.
            </p>
            <p className="mb-5" data-id="element-150">
              Por eso los límites solos no funcionan. Porque cuando pones un
              límite sin cerrar esa brecha de conexión, tu hijo/a lo vive como
              un ataque — no como un acto de amor.
            </p>
          </FadeUp>
          <FadeUp delay={0.2} data-id="element-151">
            <div className="highlight-box" data-id="element-152">
              <p className="font-bold text-lg mb-2" style={{
              color: '#8B2635'
            }} data-id="element-153">
                El Protocolo Conexión Primero™
              </p>
              <p data-id="element-154">
                El método que usamos en esta guía no empieza con límites.
                Empieza con <strong data-id="element-155">conexión</strong>. Porque cuando tu hijo/a
                siente que lo entiendes — que estás de su lado, no en su contra
                — los límites dejan de ser una guerra y se convierten en un
                acuerdo.
              </p>
              <p className="mt-3" data-id="element-156">
                Primero conexión. Luego límites. En ese orden. Siempre.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.3} data-id="element-157">
            <p className="mb-5 mt-6" data-id="element-158">Este protocolo tiene 3 fases:</p>
          </FadeUp>
          {[{
          num: '01',
          title: 'Reconectar',
          body: 'Antes de hablar de reglas, reconstruyes el puente emocional. Tu hijo/a necesita sentir que lo ves — no solo el problema.',
          icon: <Heart size={18} data-id="element-159" />
        }, {
          num: '02',
          title: 'Acordar',
          body: 'Juntos establecen límites que tienen sentido para ambos. No imposiciones — acuerdos. Eso cambia todo.',
          icon: <MessageCircle size={18} data-id="element-160" />
        }, {
          num: '03',
          title: 'Sostener',
          body: 'Tienes un sistema para mantener los límites sin conflicto, con herramientas para cuando las cosas se complican.',
          icon: <ShieldCheck size={18} data-id="element-161" />
        }].map((phase, i) => <FadeUp key={i} delay={0.35 + i * 0.1} data-id="element-162">
              <div className="flex gap-5 items-start mb-6" data-id="element-163">
                <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-playfair font-bold text-lg text-white" style={{
              backgroundColor: '#8B2635'
            }} data-id="element-164">
                  {phase.num}
                </div>
                <div data-id="element-165">
                  <p className="font-bold font-lato text-lg mb-1 flex items-center gap-2" style={{
                color: '#8B2635'
              }} data-id="element-166">
                    <span style={{
                  color: '#8B2635'
                }} data-id="element-167">
                      {phase.icon}
                    </span>
                    {phase.title}
                  </p>
                  <p data-id="element-168">{phase.body}</p>
                </div>
              </div>
            </FadeUp>)}
        </div>
      </section>

      <CTAStrip label="Quiero el Protocolo Conexión Primero™" bg="#FDF8F3" data-id="element-169" />

      {/* ── METAPHORS ── */}
      <section className="section-padding" data-id="element-170">
        <div className="max-w-2xl mx-auto" data-id="element-171">
          <FadeUp data-id="element-172">
            <h2 className="font-playfair italic text-3xl mb-8" style={{
            color: '#8B2635'
          }} data-id="element-173">
              Piénsalo así…
            </h2>
          </FadeUp>
          {[{
          icon: <Leaf size={32} style={{
            color: '#4A7C59'
          }} data-id="element-174" />,
          metaphor: 'Poner límites sin conexión es como intentar plantar una semilla en tierra seca.',
          body: 'Puedes plantar la semilla — pero sin agua, no crece. La conexión es el agua. Sin ella, los límites no echan raíces.'
        }, {
          icon: <Home size={32} style={{
            color: '#8B2635'
          }} data-id="element-175" />,
          metaphor: 'Tu relación con tu hijo/a es la casa. Los límites son las paredes.',
          body: 'Puedes construir paredes fuertes — pero si los cimientos están rotos, la casa se cae. Primero los cimientos. Luego las paredes.'
        }, {
          icon: <Compass size={32} style={{
            color: '#D4956A'
          }} data-id="element-176" />,
          metaphor: 'Criar sin un método es como navegar sin brújula.',
          body: 'Puedes moverte mucho — y agotarte mucho — sin llegar a ningún lado. Esta guía es tu brújula. Te dice exactamente hacia dónde ir y cómo llegar.'
        }].map((m, i) => <FadeUp key={i} delay={i * 0.1} data-id="element-177">
              <div className="mb-8 p-6 rounded-xl" style={{
            backgroundColor: '#FDF8F3',
            border: '1px solid #D4956A'
          }} data-id="element-178">
                <div className="mb-3" data-id="element-179">{m.icon}</div>
                <p className="font-playfair italic text-xl mb-3" style={{
              color: '#8B2635'
            }} data-id="element-180">
                  {m.metaphor}
                </p>
                <p style={{
              color: '#5A3E35'
            }} data-id="element-181">
                  {m.body}
                </p>
              </div>
            </FadeUp>)}
        </div>
      </section>

      {/* ── ORIGIN STORY ── */}
      <section className="section-padding" style={{
      backgroundColor: '#F5E6D3'
    }} data-id="element-182">
        <div className="max-w-2xl mx-auto" data-id="element-183">
          <FadeUp data-id="element-184">
            <p className="font-lato font-bold uppercase tracking-widest text-sm mb-4" style={{
            color: '#D4956A'
          }} data-id="element-185">
              Por qué creé esta guía
            </p>
            <h2 className="font-playfair italic text-3xl mb-6" style={{
            color: '#8B2635'
          }} data-id="element-186">
              La noche que me di cuenta de que lo estaba perdiendo
            </h2>
          </FadeUp>
          <FadeUp delay={0.1} data-id="element-187">
            <p className="mb-5" data-id="element-188">
              Hace tres años, mi hijo tenía 14. Y yo sentía que vivíamos en la
              misma casa pero en mundos completamente distintos.
            </p>
            <p className="mb-5" data-id="element-189">
              Cada conversación terminaba en pelea. Cada intento de poner un
              límite con el celular terminaba en portazo. Y yo me iba a dormir
              sintiéndome un fracaso como mamá.
            </p>
            <p className="mb-5" data-id="element-190">
              Una noche, después de una pelea especialmente dolorosa, me senté
              en el piso de mi cuarto y lloré. No de coraje. De miedo. Miedo de
              estar perdiendo a mi hijo en los años más importantes de su vida.
            </p>
            <p className="mb-5" data-id="element-191">
              Esa noche tomé una decisión: iba a encontrar la manera de
              reconectar con él. No importaba cuánto tiempo tomara ni cuánto
              tuviera que aprender.
            </p>
            <p className="mb-5" data-id="element-192">
              Estudié psicología del desarrollo adolescente. Hablé con
              terapeutas familiares. Leí todo lo que pude sobre neurociencia y
              hábitos digitales. Y fui probando, ajustando, equivocándome, y
              volviendo a intentarlo.
            </p>
            <p className="mb-5" data-id="element-193">
              Cuatro meses después, mi hijo y yo cenábamos juntos — sin
              celulares — y me estaba contando sobre su día. Voluntariamente.
            </p>
          </FadeUp>
          <FadeUp delay={0.2} data-id="element-194">
            <div className="pull-quote" data-id="element-195">
              "No fue magia. Fue un método. Y quiero dártelo a ti."
            </div>
          </FadeUp>
          <FadeUp delay={0.25} data-id="element-196">
            <p className="mb-5" data-id="element-197">
              Compartí lo que aprendí con otras mamás en mi comunidad. Luego con
              más. Y los resultados fueron tan consistentes que decidí
              convertirlo en una guía completa — para que ninguna mamá tenga que
              pasar por lo que yo pasé sola.
            </p>
          </FadeUp>
        </div>
      </section>

      <CTAStrip label="Quiero Reconectar con Mi Hijo/a" bg="#FDF8F3" data-id="element-198" />

      {/* ── PRODUCT REVEAL ── */}
      <section className="section-padding" data-id="element-199">
        <div className="max-w-2xl mx-auto text-center" data-id="element-200">
          <FadeUp data-id="element-201">
            <p className="font-lato font-bold uppercase tracking-widest text-sm mb-4" style={{
            color: '#D4956A'
          }} data-id="element-202">
              Te presento
            </p>
            <h2 className="font-playfair font-bold text-4xl md:text-5xl mb-4" style={{
            color: '#8B2635'
          }} data-id="element-203">
              Celular Bajo Control,
              <br data-id="element-204" />
              <span className="italic" data-id="element-205">Hogar en Paz</span>
            </h2>
            <p className="font-lato text-xl mb-8" style={{
            color: '#5A3E35'
          }} data-id="element-206">
              El plan de 30 días para poner límites sin gritos y recuperar la
              conexión con tu adolescente
            </p>
          </FadeUp>
          <FadeUp delay={0.15} data-id="element-207">
            <div className="inline-block px-8 py-6 rounded-2xl mb-8" style={{
            backgroundColor: '#F5E6D3',
            border: '2px solid #D4956A'
          }} data-id="element-208">
              <p className="font-playfair italic text-lg" style={{
              color: '#2C1810'
            }} data-id="element-209">
                Una guía digital práctica, paso a paso, diseñada específicamente
                para mamás — con estrategias basadas en psicología del
                desarrollo adolescente y miles de horas de experiencia real.
              </p>
            </div>
          </FadeUp>
        </div>
        <div className="max-w-2xl mx-auto" data-id="element-210">
          <FadeUp delay={0.2} data-id="element-211">
            <h3 className="font-playfair italic text-2xl mb-6 text-center" style={{
            color: '#8B2635'
          }} data-id="element-212">
              Qué incluye la guía
            </h3>
          </FadeUp>
          {[{
          week: 'Semana 1',
          title: 'Diagnóstico y Reconexión',
          items: ['Cómo evaluar el nivel real de dependencia digital de tu hijo/a (sin drama)', 'Las 3 conversaciones que abren la puerta — y las 3 que la cierran para siempre', 'El ejercicio de "5 minutos de presencia" que cambia la dinámica en días']
        }, {
          week: 'Semana 2',
          title: 'El Sistema de Límites',
          items: ['Cómo crear acuerdos digitales que tu hijo/a realmente respeta', 'Las palabras exactas para hablar de límites sin que se convierta en pelea', 'El método de consecuencias naturales (que funciona sin gritos ni castigos)']
        }, {
          week: 'Semana 3',
          title: 'Manejo de Crisis',
          items: ['Qué hacer cuando rompe los acuerdos (el protocolo de 4 pasos)', 'Cómo mantener la calma cuando él/ella no la tiene', 'Las señales de alerta que necesitas conocer sobre el uso problemático']
        }, {
          week: 'Semana 4',
          title: 'Sostenibilidad y Conexión Profunda',
          items: ['Cómo mantener los límites a largo plazo sin agotarte', 'Rituales familiares que fortalecen la conexión y reducen la dependencia digital', 'El plan de mantenimiento para los próximos 6 meses']
        }].map((module, i) => <FadeUp key={i} delay={i * 0.1} data-id="element-213">
              <div className="module-card mb-5" data-id="element-214">
                <div className="flex items-center gap-3 mb-3" data-id="element-215">
                  <span className="font-lato font-bold text-xs uppercase tracking-wider px-3 py-1 rounded-full text-white" style={{
                backgroundColor: '#8B2635'
              }} data-id="element-216">
                    {module.week}
                  </span>
                  <p className="font-bold font-lato text-lg" style={{
                color: '#2C1810'
              }} data-id="element-217">
                    {module.title}
                  </p>
                </div>
                <ul className="space-y-2" data-id="element-218">
                  {module.items.map((item, j) => <li key={j} className="flex gap-3 items-start" data-id="element-219">
                      <CheckCircle size={18} className="flex-shrink-0 mt-0.5" style={{
                  color: '#4A7C59'
                }} data-id="element-220" />
                      <span className="font-lato" style={{
                  color: '#5A3E35'
                }} data-id="element-221">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </FadeUp>)}
          <FadeUp delay={0.5} data-id="element-222">
            <div className="mt-8 p-6 rounded-xl" style={{
            backgroundColor: '#EAF2EC',
            border: '2px solid #4A7C59'
          }} data-id="element-223">
              <p className="font-bold font-lato text-lg mb-4 flex items-center gap-2" style={{
              color: '#4A7C59'
            }} data-id="element-224">
                <Gift size={22} style={{
                color: '#4A7C59'
              }} data-id="element-225" />{' '}
                Bonos incluidos:
              </p>
              <ul className="space-y-3" data-id="element-226">
                {['Guía de conversaciones difíciles — 12 scripts palabra por palabra para las situaciones más comunes', 'Calendario de 30 días — un paso concreto cada día, sin abrumarte', 'Checklist de señales de alerta — para saber cuándo buscar ayuda profesional'].map((bonus, i) => <li key={i} className="flex gap-3 items-start" data-id="element-227">
                    <Sparkles size={16} className="flex-shrink-0 mt-1" style={{
                  color: '#4A7C59'
                }} data-id="element-228" />
                    <span className="font-lato" data-id="element-229">{bonus}</span>
                  </li>)}
              </ul>
            </div>
          </FadeUp>
        </div>
      </section>

      <CTAStrip label="Sí, Quiero los 30 Días de Plan" bg="#F5E6D3" data-id="element-230" />

      <Divider data-id="element-231" />

      {/* ── TESTIMONIALS ── */}
      <section className="section-padding" style={{
      backgroundColor: '#F5E6D3'
    }} data-id="element-232">
        <div className="max-w-2xl mx-auto" data-id="element-233">
          <FadeUp data-id="element-234">
            <div className="flex items-center justify-center gap-2 mb-2" data-id="element-235">
              <StarRating data-id="element-236" />
              <span className="font-lato font-bold text-sm" style={{
              color: '#D4956A'
            }} data-id="element-237">
                4.9/5 — Más de 2,400 mamás
              </span>
            </div>
            <h2 className="font-playfair italic text-3xl mb-2 text-center" style={{
            color: '#8B2635'
          }} data-id="element-238">
              Lo que dicen las mamás que ya lo vivieron
            </h2>
            <p className="text-center font-lato mb-10" style={{
            color: '#7A6055'
          }} data-id="element-239">
              Resultados reales de mamás reales en Estados Unidos
            </p>
          </FadeUp>
          <div className="space-y-6" data-id="element-240">
            {[{
            name: 'Jennifer Martinez',
            location: 'Miami, FL',
            avatar: 'https://i.pravatar.cc/150?img=47',
            quote: 'Llevaba 2 años peleando con mi hija de 15 por el celular. En la primera semana de la guía tuvimos la primera conversación tranquila en meses. Lloré de alivio. Esto funciona de verdad.',
            result: 'Acuerdo digital establecido en 8 días',
            date: 'Hace 3 semanas'
          }, {
            name: 'Sandra Kowalski',
            location: 'Houston, TX',
            avatar: 'https://i.pravatar.cc/150?img=44',
            quote: 'Lo que más me sorprendió fue que no se trata de quitarle el celular — se trata de reconectar con él. Cuando empecé a aplicar las estrategias de la Semana 1, mi hijo empezó a buscarme para hablar. Algo que no pasaba desde que tenía 11 años.',
            result: 'Su hijo de 16 años redujo el uso voluntariamente',
            date: 'Hace 1 mes'
          }, {
            name: 'Maria Delgado',
            location: 'Los Angeles, CA',
            avatar: 'https://i.pravatar.cc/150?img=48',
            quote: 'Soy mamá soltera y no tenía tiempo ni energía para más conflictos. Esta guía me dio exactamente lo que necesitaba: pasos concretos, palabras exactas, y resultados rápidos. En 30 días, la paz en mi casa es completamente diferente.',
            result: 'Cero peleas por el celular en el último mes',
            date: 'Hace 2 semanas'
          }, {
            name: 'Patricia Nguyen',
            location: 'Chicago, IL',
            avatar: 'https://i.pravatar.cc/150?img=49',
            quote: 'Pensé que era demasiado tarde — mi hijo ya tenía 17 años. Pero el método funciona incluso en casos difíciles. Hoy tenemos una relación que no habíamos tenido en años. Ojalá hubiera encontrado esto antes.',
            result: 'Relación completamente transformada en 30 días',
            date: 'Hace 5 días'
          }].map((t, i) => <FadeUp key={i} delay={i * 0.1} data-id="element-241">
                <motion.div className="testimonial-card" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.08
            }} data-id="element-242">
                  <div className="flex items-start gap-4 mb-4" data-id="element-243">
                    <img src={t.avatar} alt={t.name} className="w-14 h-14 rounded-full object-cover flex-shrink-0" style={{
                  border: '3px solid #D4956A'
                }} data-id="element-244" />
                    <div className="flex-1" data-id="element-245">
                      <div className="flex items-center justify-between flex-wrap gap-1" data-id="element-246">
                        <p className="font-bold font-lato text-base" style={{
                      color: '#2C1810'
                    }} data-id="element-247">
                          {t.name}
                        </p>
                        <span className="font-lato text-xs" style={{
                      color: '#7A6055'
                    }} data-id="element-248">
                          {t.date}
                        </span>
                      </div>
                      <p className="font-lato text-sm mb-1" style={{
                    color: '#7A6055'
                  }} data-id="element-249">
                        {t.location}
                      </p>
                      <StarRating data-id="element-250" />
                    </div>
                  </div>
                  <p className="font-lato text-base mb-4" style={{
                color: '#2C1810',
                lineHeight: 1.7
              }} data-id="element-251">
                    "{t.quote}"
                  </p>
                  <div className="flex items-center gap-2" data-id="element-252">
                    <CheckCircle size={16} style={{
                  color: '#4A7C59'
                }} data-id="element-253" />
                    <span className="font-lato text-sm font-bold" style={{
                  color: '#4A7C59'
                }} data-id="element-254">
                      {t.result}
                    </span>
                  </div>
                </motion.div>
              </FadeUp>)}
          </div>
        </div>
      </section>

      <CTAStrip label="Quiero Resultados Como Estos" bg="#FDF8F3" data-id="element-255" />

      {/* ── BOLD CLAIM / MID CTA ── */}
      <section className="section-padding" style={{
      background: 'linear-gradient(135deg, #8B2635 0%, #6B1D29 100%)'
    }} data-id="element-256">
        <div className="max-w-2xl mx-auto text-center" data-id="element-257">
          <FadeUp data-id="element-258">
            <h2 className="font-playfair italic font-bold text-3xl md:text-4xl text-white mb-6" data-id="element-259">
              En 30 días puedes tener una relación completamente diferente con
              tu hijo/a.
            </h2>
            <p className="font-lato text-lg mb-8" style={{
            color: '#F5E6D3'
          }} data-id="element-260">
              Sin gritos. Sin guerras. Sin sentirte la villana. Con un método
              que funciona.
            </p>
            <CTAButton label="Sí, Quiero Empezar Hoy" data-id="element-261" />
          </FadeUp>
        </div>
      </section>

      {/* ── PRICING SECTION ── */}
      <section id="pricing-section" className="section-padding" style={{
      backgroundColor: '#FDF8F3'
    }} data-id="element-262">
        <div className="max-w-2xl mx-auto" data-id="element-263">
          <FadeUp data-id="element-264">
            <p className="font-lato font-bold uppercase tracking-widest text-sm mb-4 text-center" style={{
            color: '#D4956A'
          }} data-id="element-265">
              Oferta de lanzamiento
            </p>
            <h2 className="font-playfair italic text-3xl mb-2 text-center" style={{
            color: '#8B2635'
          }} data-id="element-266">
              Obtén acceso completo hoy
            </h2>
            <p className="text-center font-lato mb-10" style={{
            color: '#7A6055'
          }} data-id="element-267">
              Precio especial de lanzamiento — disponible por tiempo limitado
            </p>
          </FadeUp>

          <FadeUp delay={0.15} data-id="element-268">
            <div className="rounded-2xl overflow-hidden" style={{
            border: '3px solid #8B2635',
            boxShadow: '0 8px 40px rgba(139,38,53,0.15)'
          }} data-id="element-269">
              <div className="py-3 px-6 text-center font-lato font-bold text-white text-sm uppercase tracking-widest" style={{
              backgroundColor: '#8B2635'
            }} data-id="element-270">
                Precio de Lanzamiento — Acceso Inmediato
              </div>
              <div className="p-8 md:p-10" style={{
              backgroundColor: 'white'
            }} data-id="element-271">
                <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-272">
                  {/* Mockup image */}
                  <div className="flex-shrink-0" data-id="element-273">
                    <img src="https://cdn.magicpatterns.com/uploads/g9SeqrshXra3pDn6zfLqS5/Mockup.png" alt="Celular Bajo Control, Hogar en Paz — Mockup" className="rounded-lg" style={{
                    width: 180,
                    boxShadow: '4px 8px 24px rgba(44,24,16,0.15)'
                  }} data-id="element-274" />
                  </div>
                  <div className="flex-1 text-center md:text-left" data-id="element-275">
                    <p className="font-playfair font-bold text-xl mb-1" style={{
                    color: '#2C1810'
                  }} data-id="element-276">
                      Celular Bajo Control, Hogar en Paz
                    </p>
                    <p className="font-lato text-sm mb-6" style={{
                    color: '#7A6055'
                  }} data-id="element-277">
                      Guía digital completa + todos los bonos
                    </p>

                    {/* Price display */}
                    <div className="flex items-end gap-4 justify-center md:justify-start mb-6" data-id="element-278">
                      <div data-id="element-279">
                        <p className="font-lato text-sm mb-0.5" style={{
                        color: '#7A6055'
                      }} data-id="element-280">
                          Precio normal
                        </p>
                        <p className="font-lato font-bold text-2xl line-through" style={{
                        color: '#B0A09A'
                      }} data-id="element-281">
                          $12.99
                        </p>
                      </div>
                      <div className="pb-0.5" data-id="element-282">
                        <p className="font-lato text-sm mb-0.5" style={{
                        color: '#4A7C59'
                      }} data-id="element-283">
                          Hoy pagas solo
                        </p>
                        <p className="font-playfair font-bold" style={{
                        color: '#8B2635',
                        fontSize: '3.5rem',
                        lineHeight: 1
                      }} data-id="element-284">
                          $3
                          <span style={{
                          fontSize: '2rem'
                        }} data-id="element-285">
                            .99
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Savings badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 font-lato font-bold text-sm" style={{
                    backgroundColor: '#EAF2EC',
                    color: '#4A7C59'
                  }} data-id="element-286">
                      <Sparkles size={14} data-id="element-287" />
                      Ahorras $9.00 — 69% de descuento
                    </div>

                    {/* What's included summary — only 3 items */}
                    <ul className="space-y-2 mb-6 text-left" data-id="element-288">
                      {['Plan completo de 30 días (4 semanas)', 'Guía de conversaciones difíciles (12 scripts)', 'Calendario día a día + Checklist de alertas'].map((item, i) => <li key={i} className="flex items-center gap-2 font-lato text-sm" style={{
                      color: '#5A3E35'
                    }} data-id="element-289">
                          <CheckCircle size={16} style={{
                        color: '#4A7C59'
                      }} className="flex-shrink-0" data-id="element-290" />
                          {item}
                        </li>)}
                    </ul>
                  </div>
                </div>

                {/* CTA — this one goes to Hotmart */}
                <div className="mt-6 flex flex-col items-center gap-4" data-id="element-291">
                  <CTAButton label="Quiero Acceso Inmediato por $3.99" size="lg" href="https://pay.hotmart.com/O104832352I" data-id="element-292" />
                  <div className="flex items-center gap-6 flex-wrap justify-center" data-id="element-293">
                    
                    
                    
                  </div>
                </div>
              </div>
            </div>
          </FadeUp>

          {/* Urgency note */}
          <FadeUp delay={0.3} data-id="element-294">
            <div className="mt-6 flex items-start gap-3 p-4 rounded-xl" style={{
            backgroundColor: '#FFF3CD',
            border: '1px solid #D4956A'
          }} data-id="element-295">
              <AlertTriangle size={20} className="flex-shrink-0 mt-0.5" style={{
              color: '#D4956A'
            }} data-id="element-296" />
              <p className="font-lato text-sm" style={{
              color: '#5A3E35'
            }} data-id="element-297">
                <strong data-id="element-298">Nota:</strong> Este precio de lanzamiento es temporal.
                Una vez que terminemos el período de lanzamiento, el precio
                volverá a $12.99. No hay fecha exacta — puede cambiar en
                cualquier momento.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── GUARANTEE ── */}
      <section className="section-padding" style={{
      backgroundColor: '#EAF2EC'
    }} data-id="element-299">
        <div className="max-w-2xl mx-auto" data-id="element-300">
          <FadeUp data-id="element-301">
            <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-302">
              <div className="flex-shrink-0" data-id="element-303">
                <div className="guarantee-badge" data-id="element-304">
                  <ShieldCheck size={28} style={{
                  color: '#4A7C59'
                }} data-id="element-305" />
                  <p className="font-playfair font-bold text-2xl" style={{
                  color: '#4A7C59'
                }} data-id="element-306">
                    7
                  </p>
                  <p className="font-lato font-bold text-xs uppercase tracking-wider" style={{
                  color: '#4A7C59'
                }} data-id="element-307">
                    días garantía
                  </p>
                </div>
              </div>
              <div data-id="element-308">
                <h2 className="font-playfair italic text-2xl mb-4" style={{
                color: '#8B2635'
              }} data-id="element-309">
                  Mi garantía personal: 7 días o te devuelvo tu dinero
                </h2>
                <p className="mb-4" data-id="element-310">
                  Quiero que entres a esta guía sin ningún riesgo. Por eso te
                  ofrezco una garantía completa de 7 días.
                </p>
                <p className="mb-4" data-id="element-311">
                  Si aplicas el método durante 7 días y no ves ningún cambio en
                  la dinámica con tu hijo/a — te devuelvo el 100% de tu dinero.
                  Sin preguntas. Sin trámites complicados.
                </p>
                <p className="font-bold" style={{
                color: '#4A7C59'
              }} data-id="element-312">
                  Porque confío en que esto va a cambiar tu vida. Y quiero que
                  tú también confíes.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <CTAStrip label="Acceder Ahora con Garantía Total" bg="#FDF8F3" data-id="element-313" />

      {/* ── FAQ ── */}
      <section className="section-padding" style={{
      backgroundColor: '#F5E6D3'
    }} data-id="element-314">
        <div className="max-w-2xl mx-auto" data-id="element-315">
          <FadeUp data-id="element-316">
            <h2 className="font-playfair italic text-3xl mb-8 text-center" style={{
            color: '#8B2635'
          }} data-id="element-317">
              Preguntas frecuentes
            </h2>
          </FadeUp>
          {[{
          q: '¿Funciona si mi hijo/a ya es muy mayor (16, 17, 18 años)?',
          a: 'Sí. El método está diseñado para adolescentes de 10 a 18 años. Con adolescentes mayores, el enfoque de "acuerdo" funciona especialmente bien porque los trata como adultos en formación — algo que ellos valoran mucho.'
        }, {
          q: '¿Qué pasa si mi pareja no está de acuerdo con los límites?',
          a: 'La guía incluye una sección específica sobre cómo alinear a los adultos del hogar. No necesitas que todos estén de acuerdo desde el primer día — necesitas una estrategia para llegar ahí.'
        }, {
          q: '¿Cuánto tiempo tengo que dedicarle cada día?',
          a: 'Entre 15 y 30 minutos diarios. La guía está diseñada para mamás ocupadas — pasos concretos, sin relleno, sin teoría innecesaria.'
        }, {
          q: '¿Es una guía en video o en texto?',
          a: 'Es una guía digital en PDF, con ejercicios, scripts de conversación, y un calendario día a día. Puedes leerla a tu ritmo, en tu celular o computadora.'
        }, {
          q: '¿Qué pasa si ya intenté todo y nada funcionó?',
          a: 'Eso me dice que lo que has intentado no tenía el método correcto — no que el problema no tenga solución. Miles de mamás que dijeron exactamente eso hoy tienen una relación completamente diferente con sus hijos. Tú puedes ser la siguiente.'
        }].map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} index={i} data-id="element-318" />)}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="section-padding" style={{
      backgroundColor: '#FDF8F3'
    }} data-id="element-319">
        <div className="max-w-2xl mx-auto text-center" data-id="element-320">
          <FadeUp data-id="element-321">
            <h2 className="font-playfair italic text-3xl md:text-4xl mb-6" style={{
            color: '#8B2635'
          }} data-id="element-322">
              El momento de actuar es ahora
            </h2>
            <p className="font-lato text-lg mb-6" style={{
            color: '#5A3E35'
          }} data-id="element-323">
              Cada día que pasa sin un plan es un día más de conflicto,
              distancia, y agotamiento. Y cada día que pasa, tu hijo/a crece un
              poco más — y el tiempo que tienes para influir en quién se está
              convirtiendo se acorta.
            </p>
            <p className="font-lato text-lg mb-10" style={{
            color: '#5A3E35'
          }} data-id="element-324">
              No necesitas más información. Necesitas un método. Y lo tienes
              aquí.
            </p>
            <CTAButton label="Sí, Quiero Recuperar la Paz en Mi Hogar" size="lg" data-id="element-325" />
          </FadeUp>

          <FadeUp delay={0.2} data-id="element-326">
            <div className="mt-12 pt-8" style={{
            borderTop: '1px solid #D4956A'
          }} data-id="element-327">
              <p className="font-playfair italic text-xl mb-2" style={{
              color: '#8B2635'
            }} data-id="element-328">
                Con amor y esperanza,
              </p>
              <p className="font-playfair font-bold text-2xl" style={{
              color: '#2C1810'
            }} data-id="element-329">
                La autora de Celular Bajo Control, Hogar en Paz
              </p>
              <div className="mt-6 text-left max-w-lg mx-auto space-y-3" data-id="element-330">
                <p className="font-lato text-sm" style={{
                color: '#7A6055'
              }} data-id="element-331">
                  <strong style={{
                  color: '#8B2635'
                }} data-id="element-332">
                    P.D.
                  </strong>{' '}
                  — Si estás leyendo esto, es porque una parte de ti sabe que
                  algo tiene que cambiar. Confía en esa parte. Ella tiene razón.
                </p>
                <p className="font-lato text-sm" style={{
                color: '#7A6055'
              }} data-id="element-333">
                  <strong style={{
                  color: '#8B2635'
                }} data-id="element-334">
                    P.D.D.
                  </strong>{' '}
                  — Recuerda: tienes 7 días para probarlo sin ningún riesgo. Si
                  no ves resultados, te devuelvo tu dinero completo. No tienes
                  nada que perder — excepto las peleas por el celular.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-8 px-4 text-center" style={{
      backgroundColor: '#2C1810'
    }} data-id="element-335">
        <p className="font-lato text-sm" style={{
        color: '#D4956A'
      }} data-id="element-336">
          © 2024 Celular Bajo Control, Hogar en Paz · Todos los derechos
          reservados
        </p>
        <p className="font-lato text-xs mt-2" style={{
        color: '#7A6055'
      }} data-id="element-337">
          Este producto es una guía digital descargable. Los resultados
          individuales pueden variar. Los testimonios reflejan experiencias
          personales.
        </p>
      </footer>
    </div>;
}
// ─── FAQ Accordion Item ───────────────────────────────────────────────────────
function FAQItem({
  question,
  answer,
  index
}: {
  question: string;
  answer: string;
  index: number;
}) {
  const [open, setOpen] = useState(false);
  return <FadeUp delay={index * 0.07} data-id="element-338">
      <div className="mb-3 rounded-xl overflow-hidden" style={{
      border: '1px solid #D4956A'
    }} data-id="element-339">
        <button onClick={() => setOpen(!open)} className="w-full text-left px-6 py-4 flex justify-between items-center font-lato font-bold gap-4" style={{
        backgroundColor: open ? '#8B2635' : 'white',
        color: open ? 'white' : '#2C1810'
      }} data-id="element-340">
          <span data-id="element-341">{question}</span>
          {open ? <ChevronUp size={20} className="flex-shrink-0" data-id="element-342" /> : <ChevronDown size={20} className="flex-shrink-0" data-id="element-343" />}
        </button>
        <motion.div initial={false} animate={{
        height: open ? 'auto' : 0,
        opacity: open ? 1 : 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} style={{
        overflow: 'hidden'
      }} data-id="element-344">
          <div className="px-6 py-4 font-lato" style={{
          backgroundColor: 'white',
          color: '#5A3E35'
        }} data-id="element-345">
            {answer}
          </div>
        </motion.div>
      </div>
    </FadeUp>;
}